import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const W9Form = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // pending, completed, not_found
  const [landlordName, setLandlordName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    tax_name: '',
    business_name: '',
    tax_classification: '',
    llc_classification: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    tin_type: 'ein',
    tin_number: '',
    signature_name: '',
    certified: false
  });

  useEffect(() => {
    fetchW9Status();
  }, [token]);

  const fetchW9Status = async () => {
    try {
      const response = await axios.get(`${API}/w9/${token}`);
      setStatus(response.data.status);
      setLandlordName(response.data.landlord_name || '');
      if (response.data.landlord_name) {
        setForm(prev => ({ ...prev, tax_name: response.data.landlord_name }));
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setStatus('not_found');
      } else {
        setStatus('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.tax_name || !form.tax_classification || !form.address || !form.city || !form.state || !form.zip_code || !form.tin_number || !form.signature_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!form.certified) {
      toast.error('You must certify the information under penalties of perjury');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/w9/${token}/submit`, form);
      setSubmitted(true);
      toast.success('W-9 submitted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit W-9');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none";
  const selectClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none appearance-none";
  const labelClass = "block text-sm font-medium text-slate-300 mb-1.5";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0072BC] animate-spin mx-auto" />
          <p className="mt-4 text-slate-400">Loading form...</p>
        </div>
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
        <div className="glass-panel p-8 max-w-md text-center" data-testid="w9-not-found">
          <AlertCircle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Form Not Found</h1>
          <p className="text-slate-400">This W-9 form link is invalid or has expired. Please contact the SVdP volunteer who sent you this link.</p>
        </div>
      </div>
    );
  }

  if (status === 'completed' || submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
        <div className="glass-panel p-8 max-w-md text-center" data-testid="w9-success">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">W-9 Submitted</h1>
          <p className="text-slate-400 mb-4">Thank you! Your W-9 form has been submitted successfully. The SVdP team will be in touch regarding the rental assistance payment.</p>
          <p className="text-slate-500 text-sm">You may close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
      <div className="container mx-auto max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <FileText className="w-12 h-12 text-[#0072BC] mx-auto mb-3" />
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="w9-form-title">W-9 Form</h1>
          <p className="text-lg text-slate-400">Request for Taxpayer Identification Number</p>
          <p className="text-sm text-slate-500 mt-2">SVdP Mary, Mother of God Parish</p>
        </div>

        {/* Info Banner */}
        <div className="mb-6 px-4 py-3 bg-[#0072BC]/10 border border-[#0072BC]/20 rounded-xl" data-testid="w9-info-banner">
          <p className="text-sm text-slate-300">
            This form is being collected by the Society of St. Vincent de Paul to process a rental assistance payment on behalf of your tenant. Your information will only be used for payment processing purposes.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-panel p-6 md:p-8" data-testid="w9-form">
          {/* Part 1: Identification */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10">
              1. Taxpayer Identification
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Name (as shown on your income tax return) *</label>
                <input
                  type="text"
                  value={form.tax_name}
                  onChange={(e) => setForm({...form, tax_name: e.target.value})}
                  className={inputClass}
                  required
                  data-testid="w9-tax-name"
                />
              </div>
              <div>
                <label className={labelClass}>Business name / disregarded entity name (if different)</label>
                <input
                  type="text"
                  value={form.business_name}
                  onChange={(e) => setForm({...form, business_name: e.target.value})}
                  className={inputClass}
                  data-testid="w9-business-name"
                />
              </div>
              <div>
                <label className={labelClass}>Federal Tax Classification *</label>
                <select
                  value={form.tax_classification}
                  onChange={(e) => setForm({...form, tax_classification: e.target.value})}
                  className={selectClass}
                  required
                  data-testid="w9-tax-classification"
                >
                  <option value="" className="bg-slate-800">Select classification...</option>
                  <option value="Individual/sole proprietor" className="bg-slate-800">Individual / Sole Proprietor</option>
                  <option value="C Corporation" className="bg-slate-800">C Corporation</option>
                  <option value="S Corporation" className="bg-slate-800">S Corporation</option>
                  <option value="Partnership" className="bg-slate-800">Partnership</option>
                  <option value="Trust/estate" className="bg-slate-800">Trust / Estate</option>
                  <option value="LLC" className="bg-slate-800">Limited Liability Company (LLC)</option>
                  <option value="Other" className="bg-slate-800">Other</option>
                </select>
              </div>
              {form.tax_classification === 'LLC' && (
                <div>
                  <label className={labelClass}>LLC Tax Classification</label>
                  <select
                    value={form.llc_classification}
                    onChange={(e) => setForm({...form, llc_classification: e.target.value})}
                    className={selectClass}
                    data-testid="w9-llc-classification"
                  >
                    <option value="" className="bg-slate-800">Select...</option>
                    <option value="C" className="bg-slate-800">C - C Corporation</option>
                    <option value="S" className="bg-slate-800">S - S Corporation</option>
                    <option value="P" className="bg-slate-800">P - Partnership</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Part 2: Address */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10">
              2. Address
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Street Address (number, street, apt/suite) *</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({...form, address: e.target.value})}
                  className={inputClass}
                  required
                  data-testid="w9-address"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>City *</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({...form, city: e.target.value})}
                    className={inputClass}
                    required
                    data-testid="w9-city"
                  />
                </div>
                <div>
                  <label className={labelClass}>State *</label>
                  <input
                    type="text"
                    value={form.state}
                    onChange={(e) => setForm({...form, state: e.target.value})}
                    className={inputClass}
                    required
                    maxLength="2"
                    placeholder="IL"
                    data-testid="w9-state"
                  />
                </div>
                <div>
                  <label className={labelClass}>ZIP Code *</label>
                  <input
                    type="text"
                    value={form.zip_code}
                    onChange={(e) => setForm({...form, zip_code: e.target.value})}
                    className={inputClass}
                    required
                    data-testid="w9-zip"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Part 3: TIN */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10">
              3. Taxpayer Identification Number (TIN)
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>TIN Type *</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tin_type"
                      value="ssn"
                      checked={form.tin_type === 'ssn'}
                      onChange={(e) => setForm({...form, tin_type: e.target.value})}
                      className="text-[#0072BC] focus:ring-[#0072BC]"
                      data-testid="w9-tin-ssn"
                    />
                    <span className="text-slate-300">Social Security Number (SSN)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tin_type"
                      value="ein"
                      checked={form.tin_type === 'ein'}
                      onChange={(e) => setForm({...form, tin_type: e.target.value})}
                      className="text-[#0072BC] focus:ring-[#0072BC]"
                      data-testid="w9-tin-ein"
                    />
                    <span className="text-slate-300">Employer Identification Number (EIN)</span>
                  </label>
                </div>
              </div>
              <div>
                <label className={labelClass}>{form.tin_type === 'ssn' ? 'SSN' : 'EIN'} *</label>
                <input
                  type="text"
                  value={form.tin_number}
                  onChange={(e) => setForm({...form, tin_number: e.target.value})}
                  className={inputClass}
                  required
                  placeholder={form.tin_type === 'ssn' ? 'XXX-XX-XXXX' : 'XX-XXXXXXX'}
                  data-testid="w9-tin-number"
                />
                <p className="text-xs text-slate-500 mt-1">This information is securely stored and used only for payment processing.</p>
              </div>
            </div>
          </div>

          {/* Part 4: Certification & Signature */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10">
              4. Certification & Electronic Signature
            </h2>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
              <p className="text-sm text-slate-300 mb-3">Under penalties of perjury, I certify that:</p>
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-400">
                <li>The number shown on this form is my correct taxpayer identification number, and</li>
                <li>I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the IRS that I am subject to backup withholding, and</li>
                <li>I am a U.S. citizen or other U.S. person, and</li>
                <li>The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.</li>
              </ol>
            </div>

            <label className="flex items-start gap-3 p-4 bg-white/5 border border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition-colors mb-4" data-testid="w9-certify-checkbox">
              <input
                type="checkbox"
                checked={form.certified}
                onChange={(e) => setForm({...form, certified: e.target.checked})}
                className="w-5 h-5 mt-0.5 rounded border-white/20 bg-white/5 text-[#0072BC] focus:ring-[#0072BC] accent-[#0072BC]"
              />
              <span className="text-sm text-white font-medium">I certify, under penalties of perjury, that the information provided above is true, correct, and complete.</span>
            </label>

            <div>
              <label className={labelClass}>Electronic Signature (type your full legal name) *</label>
              <input
                type="text"
                value={form.signature_name}
                onChange={(e) => setForm({...form, signature_name: e.target.value})}
                className={`${inputClass} font-serif text-lg italic`}
                required
                placeholder="Type your full legal name here"
                data-testid="w9-signature"
              />
              <p className="text-xs text-slate-500 mt-1">
                By typing your name above, you are electronically signing this W-9 form. Date: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Submit */}
          <div className="text-center">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#0072BC] hover:bg-[#005a96] px-8 py-3 text-lg"
              data-testid="w9-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit W-9 Form'
              )}
            </Button>
          </div>
        </form>

        <p className="text-center text-xs text-slate-600 mt-4">
          SVdP Mary, Mother of God Parish &middot; Serving the Uptown & Edgewater communities of Chicago
        </p>
      </div>
    </div>
  );
};

export default W9Form;
